import type { CSATSurvey } from 'src/pages/csat/slice/csatTypes';
import type { CsatComplianceCategoryKey } from 'src/pages/csat/utils/csat-analysis-utils';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import {
  CSAT_ANALYSIS_METADATA_KEYS,
  CSAT_COMPLIANCE_CATEGORY_KEYS,
  formatAnalysisValue,
  pickRemainingMetadata,
} from 'src/pages/csat/utils/csat-analysis-utils';

const COMPLIANCE_LABEL_KEYS: Record<CsatComplianceCategoryKey, string> = {
  open: 'CSAT_COMPLIANCE_OPEN',
  close: 'CSAT_COMPLIANCE_CLOSE',
  communication: 'CSAT_COMPLIANCE_COMMUNICATION',
  legalDisclaimer: 'CSAT_COMPLIANCE_LEGAL_DISCLAIMER',
  responsibility: 'CSAT_COMPLIANCE_RESPONSIBILITY',
  salesProcess: 'CSAT_COMPLIANCE_SALES_PROCESS',
};

const ANALYSIS_SECTION_TITLE_KEYS: Record<(typeof CSAT_ANALYSIS_METADATA_KEYS)[number], string> = {
  keywordAnalysis: 'CSAT_ANALYSIS_KEYWORD',
  complianceAnalysis: 'CSAT_ANALYSIS_COMPLIANCE',
  semanticAnalysis: 'CSAT_ANALYSIS_SEMANTIC',
};

type Props = {
  survey: CSATSurvey;
};

export function CsatSurveyAnalysisDetails({ survey }: Props) {
  const { t } = useTranslate('translation');
  const metadata = survey.metadata ?? {};
  const analysisSections = CSAT_ANALYSIS_METADATA_KEYS.filter((key) => metadata[key] != null);

  const remaining = pickRemainingMetadata(metadata);
  const hasConfidence = metadata.confidence != null;
  const hasRemaining = Object.keys(remaining).length > 0;

  if (!analysisSections.length && !hasConfidence && !hasRemaining) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {analysisSections.map((sectionKey) => (
        <AnalysisSection
          key={sectionKey}
          title={t(ANALYSIS_SECTION_TITLE_KEYS[sectionKey])}
          data={metadata[sectionKey]}
          t={t}
        />
      ))}

      {hasConfidence && (
        <MetadataRow
          label={t('CSAT_ANALYSIS_CONFIDENCE')}
          value={
            typeof metadata.confidence === 'number'
              ? `${(metadata.confidence * 100).toFixed(0)}%`
              : formatAnalysisValue(metadata.confidence)
          }
        />
      )}

      {hasRemaining && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('CSAT_ANALYSIS_OTHER_DETAILS')}
          </Typography>
          <Stack
            spacing={1.5}
            sx={{ backgroundColor: 'background.neutral', p: 2, borderRadius: 1 }}
          >
            {Object.entries(remaining).map(([key, value]) => (
              <MetadataRow key={key} label={humanizeKey(key)} value={formatAnalysisValue(value)} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

type AnalysisSectionProps = {
  title: string;
  data: unknown;
  t: (key: string) => string;
};

function AnalysisSection({ title, data, t }: AnalysisSectionProps) {
  const complianceRows =
    data && typeof data === 'object' && !Array.isArray(data)
      ? CSAT_COMPLIANCE_CATEGORY_KEYS.filter((key) => (data as Record<string, unknown>)[key] != null)
      : [];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={1} sx={{ backgroundColor: 'background.neutral', p: 2, borderRadius: 1 }}>
        {complianceRows.length > 0 ? (
          complianceRows.map((key) => (
            <MetadataRow
              key={key}
              label={t(COMPLIANCE_LABEL_KEYS[key])}
              value={formatAnalysisValue((data as Record<string, unknown>)[key])}
            />
          ))
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {formatAnalysisValue(data)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Typography variant="body2" sx={{ minWidth: 140, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function humanizeKey(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).trim();
}
